import type { DealStageColor, DealStatusCategory } from '@veem/schema'
import { createSnapshotValue as createMutable } from '../../lib/snapshot-value.ts'

/** The status template from the CRM product spec §6.1. Admins rename and reorder these. */
export interface ProtoStage {
  /** Stable: saved views and filters point at it, so a rename never breaks them. */
  key: string
  label: string
  /** The behaviour lives here, never in the key or label (AGENTS.md §9). */
  category: DealStatusCategory
  color: DealStageColor
  /** Where a new order starts. Exactly one, and it must be an open status. */
  isDefault: boolean
  /** Hidden from the funnel's choices; orders already in it stay where they are. */
  archived: boolean
}

/** Mutable and reactive: «Налаштування» renames, reorders, hides and adds statuses. */
export const STAGES: ProtoStage[] = createMutable([] as ProtoStage[])

export const defaultStageKey = () =>
  STAGES.find((candidate) => candidate.isDefault)?.key ?? STAGES[0]?.key ?? 'lead'

/** Statuses an order can be moved into. The one it already sits in stays offered, even hidden. */
export const pickableStages = (currentKey?: string) =>
  STAGES.filter((candidate) => !candidate.archived || candidate.key === currentKey)

export const stageByKey = (key: string): ProtoStage => {
  const stage = STAGES.find((candidate) => candidate.key === key)
  if (!stage) throw new Error(`Unknown prototype stage ${key}`)
  return stage
}

export interface SellerEntity {
  id: string
  name: string
  edrpou: string | null
  /** A new client and its orders get this one unless someone picks another. */
  isDefault: boolean
  /** An inactive company stays in history but is not offered for new orders or invoices. */
  active: boolean
  /** Letterhead for КП. Images are stored objects shown through the settings API. */
  legalName?: string | null
  address?: string | null
  phone?: string | null
  email?: string | null
  website?: string | null
  altHeader?: string | null
  signatoryPosition?: string | null
  signatoryName?: string | null
  logoObjectId?: string | null
  signatureObjectId?: string | null
  stampObjectId?: string | null
}

/**
 * The four VEEM legal entities from the product spec §5.3. Mutable and reactive: «Налаштування»
 * adds, renames, deactivates them and picks the default.
 */
export const SELLER_ENTITIES: SellerEntity[] = createMutable([] as SellerEntity[])

export const sellerName = (id: string): string =>
  SELLER_ENTITIES.find((entity) => entity.id === id)?.name ?? '—'

/** Companies that can be picked for new work, plus the one a record already has. */
export const pickableSellers = (currentId?: string | null) =>
  SELLER_ENTITIES.filter((entity) => entity.active || entity.id === currentId)

export interface CatalogProduct {
  id: string
  name: string
  code: string
  kind: 'product' | 'service'
  unit: string
  /** Released technology card version. Null means engineering has to design it first. */
  bomVersion: string | null
  basePrice: string
  /**
   * The stages this product goes through, from its technology card. A seat skips machining; a
   * stainless rail skips painting. Services have no route.
   */
  route: StageKey[]
}

/**
 * Mutable and reactive: «Товари і техкарти» adds products and releases new card versions, and
 * every screen that reads a product's route or price must follow.
 */
export const PRODUCTS: CatalogProduct[] = createMutable([] as CatalogProduct[])

export const productById = (id: string): CatalogProduct => {
  const product = PRODUCTS.find((candidate) => candidate.id === id)
  if (!product) throw new Error(`Unknown prototype product ${id}`)
  return product
}

export interface RouteStage {
  /** Set once. Technology cards and orders refer to the stage by it. */
  key: string
  label: string
  workCenter: string
  /** Standard minutes per unit, for planning. Empty until the shop measures it. */
  minutes: number | null
  /** Not offered for new technology cards. Cards and orders that use it keep it. */
  archived: boolean
}

/**
 * The shop's stages (BRD §4.4), in shop order. A product's technology card picks the ones it
 * needs; a manufacturing order copies that route, so later edits never rewrite history.
 * Mutable and reactive: «Налаштування» renames, reorders, archives and adds stages.
 */
export const ROUTE_TEMPLATE: RouteStage[] = createMutable([] as RouteStage[])

/** Stages a new technology card may pick: active ones, plus any the card already has. */
export const pickableRoute = (current: string[] = []) =>
  ROUTE_TEMPLATE.filter((candidate) => !candidate.archived || current.includes(candidate.key))

/**
 * The work centers (дільниці) from the real seed, in the client's order. Admins add, rename and
 * archive them; a stage template points at one. Archived ones move to ARCHIVED_WORK_CENTERS.
 */
export const WORK_CENTERS: string[] = createMutable([] as string[])

export const ARCHIVED_WORK_CENTERS: string[] = createMutable([] as string[])

/** Machines from the real seed, each at its work center. */
export interface EquipmentItem {
  id: string
  version?: number
  name: string
  workCenter: string
  archived: boolean
}
export const EQUIPMENT: EquipmentItem[] = createMutable([] as EquipmentItem[])

export type StageKey = string

/** The product's route as template stages, in shop order. */
export const routeOf = (productId: string) => {
  const keys = productById(productId).route
  return ROUTE_TEMPLATE.filter((stage) => keys.includes(stage.key))
}

export const equipmentFor = (workCenter: string) =>
  EQUIPMENT.find((item) => !item.archived && item.workCenter === workCenter) ?? null
